import { useDeleteLearnerNoteMutation } from '@/app/redux/endpoints/learnerNotes';
import { useAppDispatch } from '@/app/redux/hooks';
import { useLearnerNotesForStudent } from '@/app/redux/hooks/learnerNotes';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { LearnerNote } from '@/common/types/learnerNote';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import { ProIcon } from '@/common/ui/UpgradeButton';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  DotsThreeVerticalIcon,
  FileTextIcon,
  NotebookIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export type ViewMode = 'gallery' | 'list';

interface LearnerNotesProps {
  studentId: string;
  organisationId: string;
  searchTerm: string;
  viewMode: ViewMode;
  onCreateNote?: () => void;
}

export default function LearnerNotes({
  studentId,
  organisationId,
  searchTerm,
  viewMode,
  onCreateNote,
}: LearnerNotesProps) {
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();
  const navigate = useNavigate();
  const { notes, isLoading } = useLearnerNotesForStudent(
    organisationId,
    studentId,
  );
  const [deleteNote] = useDeleteLearnerNoteMutation();

  useEffect(() => {
    trackMixpanelEvent('Learner Notes Viewed', { studentId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const filteredNotes = useMemo(() => {
    if (!searchTerm) return notes;
    return notes.filter((n: LearnerNote) =>
      n.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, notes]);

  async function handleDelete(note: LearnerNote) {
    try {
      await deleteNote({
        orgId: organisationId,
        studentId,
        id: note.id,
      }).unwrap();
      trackMixpanelEvent('Learner Note Deleted', {
        learnerNoteId: note.id,
        studentId,
      });
      toast.success('Note deleted');
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  }

  function handleNavigateToNote(note: LearnerNote) {
    trackMixpanelEvent('Learner Note Opened', {
      learnerNoteId: note.id,
      learnerNoteName: note.name,
      studentId,
      isSessionSummary: !!note.sessionId,
    });
    navigate(`/dashboard/learners/${studentId}/notes/${note.id}`);
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        {viewMode === 'gallery' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden p-3"
              >
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-5 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-8" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {filteredNotes.length > 0 ? (
        viewMode === 'gallery' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredNotes.map((note: LearnerNote) => (
              <NoteCard
                key={note.id}
                note={note}
                onEdit={() => handleNavigateToNote(note)}
                onDelete={() => handleDelete(note)}
              />
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNotes.map((note: LearnerNote) => (
                <NoteTableRow
                  key={note.id}
                  note={note}
                  onEdit={() => handleNavigateToNote(note)}
                  onDelete={() => handleDelete(note)}
                />
              ))}
            </TableBody>
          </Table>
        )
      ) : (
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6">
          <NotebookIcon size={80} className="text-neutral-400" weight="thin" />
          <div className="flex flex-col items-center gap-2">
            <h3 className="text-lg font-medium text-neutral-900">
              {searchTerm ? 'No matching notes' : 'No notes yet'}
            </h3>
            <p className="text-sm text-neutral-500 max-w-[400px] text-center">
              {searchTerm
                ? 'Try a different search term.'
                : "Notes for this learner will appear here once they've been created."}
            </p>
          </div>
          {!searchTerm && (
            <Button
              onClick={() =>
                isFeatureEnabled('learner-notes')
                  ? onCreateNote?.()
                  : dispatch(setAttemptedProFeature('Learner Notes'))
              }
              className="gap-2"
            >
              <PlusIcon size={16} />
              Create Note
              {!isFeatureEnabled('learner-notes') && <ProIcon />}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

interface NoteItemProps {
  note: LearnerNote;
  onEdit: () => void;
  onDelete?: () => void;
}

function NoteCard({ note, onEdit, onDelete }: NoteItemProps) {
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div
      className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
      onClick={onEdit}
    >
      <div className="h-32 flex items-center justify-center bg-gray-50">
        <FileTextIcon className="size-10 text-gray-300" />
      </div>

      <div className="p-3 border-t border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm text-gray-900 truncate">
              {note.name}
            </h3>
            <span className="text-xs text-gray-500">
              {dayjs(note.updatedAt).fromNow()}
            </span>
          </div>

          {onDelete && (
            <NoteDropdownMenu
              isOpen={isDropdownOpen}
              onOpenChange={setDropdownOpen}
              onEdit={onEdit}
              onDelete={onDelete}
              triggerClassName="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function NoteTableRow({ note, onEdit, onDelete }: NoteItemProps) {
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  return (
    <TableRow className="cursor-pointer" onClick={onEdit}>
      <TableCell>
        <div className="flex items-center gap-3 font-medium">{note.name}</div>
      </TableCell>
      <TableCell className="text-neutral-500">
        {dayjs(note.updatedAt).fromNow()}
      </TableCell>
      <TableCell className="flex justify-end">
        {onDelete && (
          <NoteDropdownMenu
            isOpen={isDropdownOpen}
            onOpenChange={setDropdownOpen}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function NoteDropdownMenu({
  isOpen,
  onOpenChange,
  onEdit,
  onDelete,
  triggerClassName,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  triggerClassName?: string;
}) {
  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={triggerClassName}
          onClick={(e) => e.stopPropagation()}
        >
          <DotsThreeVerticalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem
          className="flex gap-2"
          onClick={() => {
            onOpenChange(false);
            onEdit();
          }}
        >
          <PencilSimpleIcon size={16} /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="flex gap-2 text-red-600"
          onClick={() => {
            onOpenChange(false);
            onDelete();
          }}
        >
          <TrashIcon size={16} /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
